import { AppConfigFile, useAppConfig } from "@hooks/http/configs";
import { LoadingOverlay } from "@repo/ui/core";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";

// Load custom env
const {
  NA_AUTH_URL,
  NA_AUTH_REALM,
  NA_AUTH_CLIENT_ID,
  NA_AAOSP_FIXED_URL,
  NA_CTRL_API_URL,
  NA_CTRL_GRPC_URL,
  NA_WORKSPACE_PROXY_URL,
  NA_FARO_URL,
  NA_FARO_ENV,
  NA_POSTHOG_HOST,
  NA_POSTHOG_KEY,
  NA_POSTHOG_PROPERTIES,
} = import.meta.env as unknown as AppEnv;

// load tooling env
const { DEV, PROD } = import.meta.env;

/**
 * placholder for app specific context and types
 */
export interface AppEnv {
  /** placeholder env vars */
  NA_?: string;
  NA_AUTH_URL: string;
  NA_AUTH_REALM: string;
  NA_AUTH_CLIENT_ID: string;
  NA_CTRL_API_URL: string;
  /**
   * Base URL of ctrl's gRPC-Web listener (VHAL pass-through). Optional;
   * defaults to `<NA_CTRL_API_URL>/grpc` when not set.
   */
  NA_CTRL_GRPC_URL?: string;
  NA_AAOSP_FIXED_URL: string;
  NA_WORKSPACE_PROXY_URL: string;
  /**
   * Grafana Faro receiver collect URL for browser monitoring. Optional;
   * monitoring is off when unset.
   */
  NA_FARO_URL?: string;
  /** Environment label on browser monitoring data (dev, demo, …). */
  NA_FARO_ENV?: string;
  /** PostHog ingest host. Defaults to PostHog Cloud EU when a key is set. */
  NA_POSTHOG_HOST?: string;
  /** PostHog project API key. Absent or empty disables product analytics. */
  NA_POSTHOG_KEY?: string;
  /** JSON object of static event properties, e.g. {"hub":"..","cell":"..","env":".."}. */
  NA_POSTHOG_PROPERTIES?: string;
}

/**
 * placeholder for app specific feature flags
 */
export interface AppFeatures {
  featureA?: boolean;
  featureB?: boolean;
}

export interface App {
  prod: boolean;
  dev: boolean;
  env: AppEnv;
  features: AppFeatures;
  initialized: boolean;
}

export const AppContext = createContext<App>({} as App);
export function useAppContext(): App {
  return useContext(AppContext);
}

/**
 * Provide application configuration context
 * @returns
 */
export const AppProvider: React.FC<PropsWithChildren> = ({ children }) => {
  // init env from file defaults
  // const manifest = useRef<string>()
  // const [isStale, setIsStale] = useState(false)
  const [initialized, setInitialized] = useState(false);
  const [env, setEnv] = useState<AppEnv>({
    NA_AUTH_URL,
    NA_AUTH_REALM,
    NA_AUTH_CLIENT_ID,
    NA_CTRL_API_URL,
    NA_CTRL_GRPC_URL,
    NA_AAOSP_FIXED_URL,
    NA_WORKSPACE_PROXY_URL,
    NA_FARO_URL,
    NA_FARO_ENV,
    NA_POSTHOG_HOST,
    NA_POSTHOG_KEY,
    NA_POSTHOG_PROPERTIES,
  });

  const applyRemoteEnv = useCallback(
    (config: Partial<AppEnv>) =>
      setEnv((base) => ({
        ...base,
        ...config,
      })),
    [],
  );

  // fetcher for app manifest (to determine updates)
  // const {
  //   data: manifestData,
  //   isLoading: manifestLoading,
  //   error: manifestError,
  // } = useHttp("manifest.json", {
  //   http: {},
  //   swr: { refreshInterval: HOUR_HALF },
  // });

  // fetcher for app env configuration
  const {
    data: envData,
    // isLoading: envLoading,
    error: envError,
  } = useAppConfig(AppConfigFile.ENV, {
    refreshInterval: 0,
  });

  // effect state when env data is loaded
  useEffect(() => {
    if (envData) {
      applyRemoteEnv(envData);
      setInitialized(true);
    } else if (envError && DEV) {
      // case when dev mode has no env.json but uses .env.local instead
      setInitialized(true);
    }
  }, [envData, envError, applyRemoteEnv]);

  const features = useMemo<AppFeatures>(() => {
    // derive features from env or manifest as needed
    return {
      // example only
      featureA: env.NA_ === "true",
    };
  }, [env]);

  return (
    <AppContext.Provider
      value={{
        dev: DEV,
        prod: PROD,
        env,
        features,
        initialized,
      }}
    >
      <LoadingOverlay visible={!initialized} loaderProps={{ type: "bars" }} />
      {initialized && children}
    </AppContext.Provider>
  );
};
